import {
  averageSpeed,
  buildLegs,
  type CpComparison,
  type CpPerformance,
  type CpTerms,
  cpComparison,
  steamingHours,
} from './calc.js';
import { reportConsumption } from './fuel.js';
import type { VoyageReportKind } from './schemas.js';

/**
 * Rapor rapor döküm ve CP "iyi hava günü" değerlendirmesi (ADR 0022 §4, ADR 0024 #17). Sefer ve
 * CP penceresi toplamları `voyagePerformance`'tadır; burada rapor rapor bacaklar hesaplanır,
 * formül aynı (`steamingHours`, `averageSpeed`). Saf fonksiyonlar.
 */

/** Rapor: kimliği ve durumu ile; yalnız gönderilmişler hesaba girer (ADR 0022 §3). */
export interface LegReport {
  id: string;
  kind: VoyageReportKind;
  reportedAt: string;
  status: string;
  data: Readonly<Record<string, unknown>>;
}

const round = (value: number, digits = 3) => {
  if (!Number.isFinite(value)) return value;
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

/**
 * Douglas deniz durumu 0–8 için rüzgâr denizi yüksekliği (m): ölçeğin üst sınırı, en sıkı okuma
 * (ADR 0024 #17). Deniz durumu 9 ("phenomenal", 14 m üstü) her sınırı aşar.
 */
export const DOUGLAS_WIND_SEA_M = [0, 0.1, 0.5, 1.25, 2.5, 4, 6, 9, 14] as const;

/** Deniz durumunun rüzgâr denizi yüksekliği; 9 sonsuz, ölçek dışı değer `null`. */
export function windSeaHeight(seaState: number): number | null {
  if (seaState === 9) return Number.POSITIVE_INFINITY;
  return Number.isInteger(seaState) ? (DOUGLAS_WIND_SEA_M[seaState] ?? null) : null;
}

/**
 * Belirgin dalga yüksekliği Hs = √(rüzgâr denizi² + swell²) (ADR 0024 #17). Deniz durumu ya da
 * swell yoksa `null`: bilinmiyor.
 */
export function significantWaveHeight(
  seaState: number | null,
  swellHeightM: number | null,
): number | null {
  if (seaState === null || swellHeightM === null) return null;
  const windSea = windSeaHeight(seaState);
  if (windSea === null) return null;
  return round(Math.sqrt(windSea ** 2 + swellHeightM ** 2));
}

const byTime = (a: LegReport, b: LegReport) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt);

const num = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Raporun "son rapordan bu yana" mesafesi: Noon'da seyir bölümünde, olaylarda gövdede. */
function distanceOf(report: LegReport): number | null {
  const source = (report.kind === 'noon' ? report.data.navigation : report.data) as
    | Record<string, unknown>
    | undefined;
  return num(source?.distanceSinceLast);
}

/** Noon yakıt satırlarının tüketim toplamı (MT; `reportConsumption`); satır yoksa `null`. */
function consumptionOf(report: LegReport | undefined): number | null {
  if (report?.kind !== 'noon') return null;
  const rows = reportConsumption(report);
  if (rows.length === 0) return null;
  return round(rows.reduce((sum, [, value]) => sum + value, 0));
}

export interface SpeedLeg {
  id: string;
  kind: VoyageReportKind;
  reportedAt: string;
  hours: number | null;
  distanceNm: number | null;
  avgSpeedKn: number | null;
}

/** Rapor rapor bacaklar: yalnız gönderilmiş raporlar (kesin kayıt; ADR 0022 §3). */
export function speedLegs(reports: readonly LegReport[]): SpeedLeg[] {
  const sorted = reports.filter((r) => r.status === 'submitted').sort(byTime);
  return sorted.map((report, index) => {
    const previous = sorted[index - 1];
    const hours = previous ? steamingHours(previous.reportedAt, report.reportedAt) : null;
    const distanceNm = distanceOf(report);
    return {
      id: report.id,
      kind: report.kind,
      reportedAt: report.reportedAt,
      hours,
      distanceNm,
      avgSpeedKn: distanceNm === null ? null : averageSpeed(distanceNm, hours),
    };
  });
}

export interface CpDay extends SpeedLeg {
  beaufort: number | null;
  /** Douglas deniz durumu 0–9. */
  seaState: number | null;
  swellHeightM: number | null;
  /** Belirgin dalga yüksekliği Hs (`significantWaveHeight`); deniz durumu 9 ise sonsuz. */
  waveHeightM: number | null;
  /** Noon yakıt satırlarının tüketim toplamı (MT); satır yoksa `null`. */
  consumptionMt: number | null;
  /**
   * Bacak boğaz, kanal, demir, sürüklenme ya da bekleme açıkken bitti: CP performans süresine
   * girmez (kural 7, 10, 11), iyi hava değerlendirmesine de girmez.
   */
  excluded: boolean;
  /** Ortalama hız CP hızının altında değil; CP hızı yoksa `null`. */
  speedOk: boolean | null;
  /**
   * CP hava sınırları içinde mi ("iyi hava günü"; ADR 0024 #17): Beaufort ≤ CP sınırı ve Hs ≤ CP
   * dalga sınırı, her biri yalnız sınır tanımlıysa. Sınır varken değer (Beaufort, deniz durumu ya
   * da swell) girilmemişse `null`: iyi hava sayılmaz (en temkinli okuma). Sınır yoksa `null`.
   */
  goodWeather: boolean | null;
}

export interface CpWeatherLimits {
  /** Gün gün hız karşılaştırması için CP hızı (yük durumuna göre). */
  plannedSpeedKn: number | null;
  maxBeaufort: number | null;
  maxWaveHeightM: number | null;
  /**
   * CP kaydının ek sınırları (ADR 0084 §1): Douglas deniz durumu ve swell. Verilmezse ya da boşsa
   * denetlenmez; eski çağrılar (seferin CP alanları) aynı sonucu verir.
   */
  maxDouglas?: number | null;
  maxSwellM?: number | null;
}

/** Sınır denetimlerinin sonucu: biri aşılmışsa `false`, biri bilinmiyorsa `null`. */
function verdict(checks: readonly (boolean | null)[]): boolean | null {
  if (checks.length === 0) return null;
  if (checks.includes(false)) return false;
  return checks.includes(null) ? null : true;
}

/**
 * CP penceresindeki (COSP → EOSP) Noon raporları; hız ve hava CP sınırlarıyla karşılaştırılır
 * (ADR 0024 #17). Yalnız gönderilmiş raporlar.
 */
export function cpDays(
  reports: readonly LegReport[],
  window: { start: string; end: string | null },
  limits: CpWeatherLimits,
): CpDay[] {
  const start = Date.parse(window.start);
  const end = window.end ? Date.parse(window.end) : Number.POSITIVE_INFINITY;
  const byId = new Map(reports.map((r) => [r.id, r]));
  // Segment açıkken biten bacaklar: CP penceresi hesabıyla aynı kural (`voyagePerformance`)
  const sorted = reports.filter((r) => r.status === 'submitted').sort(byTime);
  const legs = buildLegs(sorted);
  const excluded = new Set(
    sorted.filter((_, i) => (legs[i]?.open.size ?? 0) > 0).map((report) => report.id),
  );
  return speedLegs(reports)
    .filter((leg) => {
      const at = Date.parse(leg.reportedAt);
      return leg.kind === 'noon' && at > start && at <= end;
    })
    .map((leg) => {
      const report = byId.get(leg.id);
      const weather = (report?.data.weather ?? {}) as {
        wind?: { beaufort?: unknown };
        sea?: { state?: unknown; swellHeightM?: unknown };
      };
      const beaufort = num(weather.wind?.beaufort);
      const seaState = num(weather.sea?.state);
      const swellHeightM = num(weather.sea?.swellHeightM);
      const waveHeightM = significantWaveHeight(seaState, swellHeightM);
      const checks: (boolean | null)[] = [];
      if (limits.maxBeaufort !== null) {
        checks.push(beaufort === null ? null : beaufort <= limits.maxBeaufort);
      }
      if (limits.maxWaveHeightM !== null) {
        checks.push(waveHeightM === null ? null : waveHeightM <= limits.maxWaveHeightM);
      }
      if (limits.maxDouglas !== undefined && limits.maxDouglas !== null) {
        checks.push(seaState === null ? null : seaState <= limits.maxDouglas);
      }
      if (limits.maxSwellM !== undefined && limits.maxSwellM !== null) {
        checks.push(swellHeightM === null ? null : swellHeightM <= limits.maxSwellM);
      }
      return {
        ...leg,
        beaufort,
        seaState,
        swellHeightM,
        waveHeightM,
        consumptionMt: consumptionOf(report),
        excluded: excluded.has(leg.id),
        speedOk:
          limits.plannedSpeedKn === null || leg.avgSpeedKn === null
            ? null
            : leg.avgSpeedKn >= limits.plannedSpeedKn,
        goodWeather: verdict(checks),
      };
    });
}

/** Bir dönemin CP değerleri ve CP ile karşılaştırması (`cpComparison`; tolerans yok). */
export interface CpPeriodValues {
  hours: number;
  distanceNm: number;
  avgSpeedKn: number | null;
  dailyConsumptionMt: number | null;
  /** Yük durumu (laden/ballast) bilinmiyorsa `null`. */
  comparison: CpComparison | null;
}

export interface CpEvaluation {
  /** Esas değer: yalnız iyi hava günleri ("good weather days" yöntemi; ADR 0024 #17). */
  goodWeather: CpPeriodValues & { days: number };
  /** Bütün CP dönemi (COSP → EOSP, hariç aralıklar düşülmüş; `voyagePerformance`). */
  whole: CpPeriodValues;
}

/**
 * CP değerlendirmesi, sektördeki "good weather days" yöntemiyle (ADR 0024 #17): hız = iyi hava
 * günlerindeki mesafe / süre; tüketim = iyi hava günlerindeki tüketim / süre × 24. Hesaba
 * yalnız iyi hava olduğu bilinen (`goodWeather === true`), hariç tutulmayan ve süresi olan
 * günler girer; havası bilinmeyen gün girmez (en temkinli okuma). Tüketimi olmayan gün yalnız
 * tüketim hesabından düşer. Karşılaştırma ve bütün CP dönemi `cpComparison` ile aynı.
 */
export function cpEvaluation(
  days: readonly CpDay[],
  window: Pick<
    CpPerformance,
    'hours' | 'distanceNm' | 'avgSpeedKn' | 'dailyConsumptionMt' | 'loadCondition'
  >,
  terms: CpTerms,
): CpEvaluation {
  const counted = days.filter(
    (d): d is CpDay & { hours: number; distanceNm: number } =>
      d.goodWeather === true && !d.excluded && d.hours !== null && d.distanceNm !== null,
  );
  let hours = 0;
  let distanceNm = 0;
  let fuelHours = 0;
  let consumption = 0;
  for (const day of counted) {
    hours += day.hours;
    distanceNm += day.distanceNm;
    if (day.consumptionMt !== null) {
      fuelHours += day.hours;
      consumption += day.consumptionMt;
    }
  }
  const avgSpeedKn = averageSpeed(distanceNm, hours);
  const dailyConsumptionMt = fuelHours > 0 ? round((consumption / fuelHours) * 24) : null;
  return {
    goodWeather: {
      days: counted.length,
      hours: round(hours),
      distanceNm: round(distanceNm),
      avgSpeedKn,
      dailyConsumptionMt,
      comparison: cpComparison(
        { avgSpeedKn, dailyConsumptionMt, loadCondition: window.loadCondition },
        terms,
      ),
    },
    whole: {
      hours: window.hours,
      distanceNm: window.distanceNm,
      avgSpeedKn: window.avgSpeedKn,
      dailyConsumptionMt: window.dailyConsumptionMt,
      comparison: cpComparison(window, terms),
    },
  };
}
